import { isStaleSession, StaleSessionError, tickets, type Ticket, type UpdateTicketData } from '@field-tickets/shared';
import { getAuthEpoch } from '@/utils/authEpoch';
import type { TicketSaveLease } from '@/utils/offlineQueue';
import { anyTicketRecoveryDraft } from '@/utils/ticketRecovery';

/**
 * The one way the web app sends a ticket PUT (#967). The autosave (through
 * useUpdateTicket's `put`), the close-time flushes (QuickBooks-linked or not)
 * and the offline replay all call it; nothing else calls `tickets.update`.
 *
 * Inside the ticket's save lock, at the request boundary (the API client's
 * `beforeSend`, called synchronously right before `fetch`, after every await
 * of the request setup):
 * - the lease must still be active (the caller holds the lock now);
 * - the session epoch must still be the one the operation started in;
 * - no recovery draft of the ticket may exist, read from storage, so a draft
 *   another tab or the replay wrote while this caller waited holds the PUT.
 * A failed check rejects with nothing sent.
 *
 * An answer (success or failure) that arrives after the session epoch moved
 * is replaced by a StaleSessionError, so no caller acts on it.
 */

export class TicketPutBlockedError extends Error {
  readonly code = 'ticket_put_blocked';
  constructor(readonly ticketId: string) {
    super(`Ticket ${ticketId} is waiting for a reload; nothing was sent`);
    this.name = 'TicketPutBlockedError';
  }
}

export function isTicketPutBlocked(err: unknown): err is TicketPutBlockedError {
  return (err as { code?: string } | null)?.code === 'ticket_put_blocked';
}

export { isStaleSession, StaleSessionError };

export function isActorMismatch(error: unknown): boolean {
  const data = (error as { data?: { code?: string; error?: string } } | null)?.data;
  return data?.code === 'actor_mismatch' || data?.error === 'actor_mismatch';
}

let sending = 0;
let gateChecked = 0;

/** True only while a sendTicketPut call is in progress (for tests). */
export function isSendingThroughTicketPutGate(): boolean {
  return sending > 0;
}

/** How many requests passed the gate's boundary check (for tests). */
export function ticketPutGateChecks(): number {
  return gateChecked;
}

export async function sendTicketPut(
  lease: TicketSaveLease,
  payload: UpdateTicketData,
  options: { epoch: number; owner: string; signal?: AbortSignal },
): Promise<Ticket> {
  const beforeSend = () => {
    if (!options.owner) throw new StaleSessionError();
    if (!lease.active) {
      throw new Error(`Ticket ${lease.ticketId} PUT outside its save lock`);
    }
    if (getAuthEpoch() !== options.epoch) throw new StaleSessionError();
    if (anyTicketRecoveryDraft(lease.ticketId, options.owner)) throw new TicketPutBlockedError(lease.ticketId);
  };
  // Checked now, and again at the request boundary: the API client may
  // await (the auth token) before the request goes out.
  beforeSend();
  let answer: Ticket;
  sending += 1;
  try {
    answer = await tickets.update(lease.ticketId, payload, options.signal, (headers) => {
      beforeSend();
      headers['X-Expected-User-Id'] = options.owner;
      gateChecked += 1;
    });
  } catch (err) {
    if (getAuthEpoch() !== options.epoch) throw new StaleSessionError();
    throw err;
  } finally {
    sending -= 1;
  }
  if (getAuthEpoch() !== options.epoch) throw new StaleSessionError();
  return answer;
}
